import { Button, styleConstants, useFormatDate } from '@hwm/ui-lib';
import { t, Trans } from '@lingui/macro';
import { makeStyles, useTheme } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { endOfDay, isAfter, startOfDay, subDays, subMonths, subYears } from 'date-fns';
import { addDays, isPast } from 'date-fns/esm';
import { useCallback, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import { DatePickerCustomHeader } from './DatePickerCustomHeader';

const useStyles = makeStyles((theme) => ({
  datePickerContainer: ({ minWidth }: { minWidth: number }) => ({
    width: '100%',
    minHeight: 40,
    height: 40,
    position: 'relative',
    '& .react-datepicker-wrapper': {
      display: 'block',
      height: '100%',
      minWidth: minWidth,
      '& input': {
        minWidth: minWidth,
      },
    },
    '& .react-datepicker__input-container': {
      display: 'block',
      height: '100%',
    },
  }),
  input: {
    borderRadius: '5px',
    height: 40,
    fontWeight: 600,
    padding: theme.spacing(0, 3),
    width: '100%',
    zIndex: 1,
    cursor: 'pointer',
    outline: 'none',
    boxSizing: 'border-box',
    '& :focus': {
      outline: 'none',
    },
    '&::-ms-clear': {
      display: 'none',
    },
  },
  closeIcon: {
    position: 'absolute',
    right: 8,
    top: 8,
    zIndex: 10,
    cursor: 'pointer',
  },
  calendarContainer: {
    '& .react-datepicker__day--keyboard-selected': {
      backgroundColor: 'transparent',
      color: 'inherit',
      border: 'none',
      outline: 'none',
    },

    '& .react-datepicker__day--selected': {
      backgroundColor: styleConstants.colors.primaryColor,
    },
    '& .react-datepicker__day--in-range': {
      backgroundColor: styleConstants.colors.primaryColor,
    },
    '& .react-datepicker__day--in-selecting-range': {
      backgroundColor: styleConstants.colors.primaryColor + '66',
    },
    '& .react-datepicker__day--in-selecting-range:hover': {
      backgroundColor: styleConstants.colors.primaryColor,
    },
    '& .react-datepicker__time-list-item--selected': {
      backgroundColor: `${styleConstants.colors.primaryColor} !important`,
    },
  },
}));

interface DateRangePickerProps {
  placeholder?: string;
  value?: [Date, Date];
  onChange?: (dates: [Date, Date] | null) => void;
  minDate?: Date;
  newEditableLook?: boolean;
  last3Months?: boolean;
  last6Months?: boolean;
  last12Months?: boolean;
  last5Years?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  maxTotalRangeInDays?: number;
  monthAndYearSelect?: boolean;
  customMinWidth?: number;
  dataTestId?: string;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  placeholder = t`Select dates`,
  value,
  onChange,
  minDate = undefined,
  newEditableLook = false,
  last3Months = false,
  last6Months = false,
  last12Months = false,
  last5Years = false,
  disabled = false,
  clearable = true,
  maxTotalRangeInDays = Infinity,
  monthAndYearSelect = false,
  customMinWidth = 220,
  dataTestId,
}) => {
  const theme = useTheme();
  const styles = useStyles({ minWidth: customMinWidth });

  const { formatDate } = useFormatDate();

  const [open, setOpen] = useState<boolean>(false);
  const [startDate, setStartDate] = useState(value?.[0] ? startOfDay(value[0]) : null);
  const [endDate, setEndDate] = useState(value?.[0] ? endOfDay(value[1]) : null);
  const [customMaxRange, setCustomMaxRange] = useState<[Date, Date]>([
    subDays(value?.[0], Infinity),
    addDays(value?.[0], Infinity),
  ]);

  const _onChange = useCallback(
    (dates: [Date, Date]) => {
      const [startNotFormatted, endNotFormatted] = dates;

      const [start, end] = [startOfDay(startNotFormatted), endNotFormatted ? endOfDay(endNotFormatted) : null];

      setStartDate(start);
      setEndDate(end);

      if (start && !end) setCustomMaxRange([subDays(start, maxTotalRangeInDays), addDays(start, maxTotalRangeInDays)]);

      if (end) {
        setCustomMaxRange([subDays(start, Infinity), addDays(start, Infinity)]);
      }

      start && end && onChange?.([start, end]);
    },
    [maxTotalRangeInDays, setCustomMaxRange, setStartDate, setEndDate, onChange]
  );

  useEffect(() => {
    setStartDate(value ? startOfDay(value[0]) : null);
    setEndDate(value ? endOfDay(value[1]) : null);
  }, [value]);

  const setDates = useCallback(
    ([start, end]: [Date, Date]) => {
      setStartDate(start);
      setEndDate(end);
      setCustomMaxRange([subDays(start, Infinity), addDays(end, Infinity)]);
      start && end && onChange && onChange([start, end]);
      setOpen(false);
    },
    [setStartDate, setEndDate, onChange, setCustomMaxRange]
  );

  const setToday = useCallback(() => {
    setDates([startOfDay(new Date()), endOfDay(new Date())]);
  }, [setDates]);

  const setLast7Days = useCallback(() => {
    setDates([subDays(startOfDay(new Date()), 7), endOfDay(new Date())]);
  }, [setDates]);

  const setLast30Days = useCallback(() => {
    setDates([subDays(startOfDay(new Date()), 30), endOfDay(new Date())]);
  }, [setDates]);

  const setLast3Months = useCallback(() => {
    setDates([subMonths(startOfDay(new Date()), 3), endOfDay(new Date())]);
  }, [setDates]);

  const setLast6Months = useCallback(() => {
    setDates([subMonths(startOfDay(new Date()), 6), endOfDay(new Date())]);
  }, [setDates]);

  const setLast12Months = useCallback(() => {
    setDates([subMonths(startOfDay(new Date()), 12), endOfDay(new Date())]);
  }, [setDates]);

  const setLast5Years = useCallback(() => {
    setDates([subYears(startOfDay(new Date()), 5), endOfDay(new Date())]);
  }, [setDates]);

  const inputValue = startDate && endDate ? formatDate(startDate, 'P') + ' - ' + formatDate(endDate, 'P') : null;

  return (
    <div className={`${styles.datePickerContainer}`} data-testid={dataTestId}>
      <DatePicker
        selected={startDate}
        onChange={_onChange}
        startDate={startDate}
        endDate={endDate}
        disabledKeyboardNavigation={true}
        open={open}
        onInputClick={() => {
          setStartDate(value ? startOfDay(value[0]) : null);
          setEndDate(value ? endOfDay(value[1]) : null);
          setOpen(true);
        }}
        disabled={disabled}
        selectsRange
        shouldCloseOnSelect={startDate && !endDate}
        value={inputValue}
        placeholderText={placeholder}
        minDate={minDate ?? customMaxRange[0]}
        maxDate={customMaxRange[1]}
        portalId="root"
        calendarClassName="z-50"
        //
        renderCustomHeader={(props) => <DatePickerCustomHeader {...props} monthAndYearSelect={monthAndYearSelect} />}
        //
        onCalendarClose={() => {
          if (!startDate || !endDate) {
            setStartDate(value ? startOfDay(value[0]) : null);
            setEndDate(value ? endOfDay(value[1]) : null);
          }

          if (value) {
            setCustomMaxRange([subDays(value[0], Infinity), addDays(value[1], Infinity)]);
          }

          setOpen(false);
        }}
        calendarContainer={({ children }) => (
          <div className={`bg-white flex flex-col shadow-md ${styles.calendarContainer} z-50`}>
            {children}
            <div className="flex flex-wrap mb-2 max-w-xs justify-center">
              <Button style={{ height: 28 }} className="m-0.5" onClick={setToday}>
                <Trans>Today</Trans>
              </Button>
              <Button style={{ height: 28 }} className="m-0.5" onClick={setLast7Days}>
                <Trans>Last 7 days</Trans>
              </Button>
              <Button style={{ height: 28 }} className="m-0.5" onClick={setLast30Days}>
                <Trans>Last 30 days</Trans>
              </Button>
              {last3Months && (
                <Button style={{ height: 28 }} className="m-0.5" onClick={setLast3Months}>
                  <Trans>Last 3 months</Trans>
                </Button>
              )}
              {last6Months && (
                <Button style={{ height: 28 }} className="m-0.5" onClick={setLast6Months}>
                  <Trans>Last 6 months</Trans>
                </Button>
              )}
              {last12Months && (
                <Button style={{ height: 28 }} className="m-0.5" onClick={setLast12Months}>
                  <Trans>Last 12 months</Trans>
                </Button>
              )}
              {last5Years && (
                <Button style={{ height: 28 }} className="m-0.5" onClick={setLast5Years}>
                  <Trans>Last 5 years</Trans>
                </Button>
              )}
            </div>
          </div>
        )}
        filterDate={(date) => isPast(date) && isAfter(date, new Date(2014, 11, 31))}
        customInput={
          <input
            type="text"
            className={styles.input}
            readOnly
            style={{
              color: '#333333',
              textShadow: '0 0 0 ' + theme.palette.text.secondary,
              border: disabled
                ? '2px solid #fff'
                : newEditableLook
                  ? `2px solid ${styleConstants.colors.primaryColor}`
                  : '2px solid #fff',
              borderRadius: disabled ? 0 : '',
              backgroundColor: disabled ? '#F0F1F5' : '',
              cursor: disabled ? '' : 'pointer',
            }}
          />
        }
      />
      {clearable && startDate && endDate && (
        <Close
          className={styles.closeIcon}
          onClick={() => {
            onChange && onChange(null);
          }}
        />
      )}
    </div>
  );
};
